import * as React from 'react';
import { AppBar, Drawer, Menu, Popover } from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { COLOR, TRANSPARENT_COLOR } from '@src/styles/color';
import {
  BOX_SHADOW,
  BORDER_RADIUS,
  SIDEBAR_WIDTH,
  TOOLTIP_BOX_SHADOW,
  MOBILE_BREAKPOINT,
  SIDEBAR_WIDTH_COLLAPSED,
  NEW_SIDEBAR_WIDTH,
  FLASH_SALE_BANNER_HEIGHT,
} from '@src/styles/config';

const StyledWrapper = styled('div')`
  position: relative;
  top: ${(props) =>
    props.headerVoucher ? `${FLASH_SALE_BANNER_HEIGHT}px` : 0};
  height: ${(props) =>
    props.headerVoucher
      ? `calc(100dvh - ${FLASH_SALE_BANNER_HEIGHT}px)`
      : `100dvh`};

  &:after {
    display: table;
    clear: both;
    content: '';
  }

  @media only screen and (min-width: ${MOBILE_BREAKPOINT}) {
    .web {
      display: block;
    }

    .mobile {
      display: none;
    }
  }

  @media only screen and (max-width: ${MOBILE_BREAKPOINT}) {
    .small-username-info {
      display: none;
    }
    .web {
      display: none;
    }

    .mobile {
      display: block;
    }
  }
`;

const StyledWrapperExpiringSoon = styled('div')`
  height: 40px;
  padding: 8px 16px;
  box-sizing: border-box;
  background-color: #ff692e;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const sidebarWidth = ({ openSidebar }) =>
  openSidebar ? NEW_SIDEBAR_WIDTH : SIDEBAR_WIDTH_COLLAPSED;

const StyledMainPanel = styled('div')(
  ({
    theme,
    openSidebar,
    isBackgroundWhiteMobile,
    isWhiteBackgroundPage,
    withPaneluseMultiBlockUIV2,
  }) => ({
    [theme.breakpoints.up('md')]: {
      width: `calc(100% - ${
        withPaneluseMultiBlockUIV2 || sidebarWidth({ openSidebar })
      }px)`,
    },
    [theme.breakpoints.down('md')]: {
      width: `calc(100% - ${SIDEBAR_WIDTH_COLLAPSED}px)`,
    },
    [theme.breakpoints.down('sm')]: {
      width: `calc(100% )`,
      backgroundColor: isBackgroundWhiteMobile ? COLOR.white : 'transparent',
    },
    // if use lib react-joyride, it will make overflow is initial => use important to fix this
    overflow: 'auto !important',
    position: 'relative',
    float: 'right',
    maxHeight: '100%',
    height: '100%',
    width: '100%',
    ...(isWhiteBackgroundPage && {
      backgroundColor: COLOR.white,
    }),
  }),
);

const StyledNewAppBar = styled(AppBar)`
  background: transparent;
  box-shadow: none;
  position: absolute;
  width: 100%;
  min-height: 64px;
  display: none;
  position: sticky;
  top: 0px;

  .container {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    min-height: 64px;
    padding-left: 16px;
    padding-right: 16px;
    border-radius: ${BORDER_RADIUS};
    color: ${COLOR.text};
    box-shadow: ${BOX_SHADOW};
    background-color: ${COLOR.white};
    box-sizing: border-box;
  }

  z-index: ${(props) => (props.runningTourGuide ? -1000 : 1029)};
  opacity: ${(props) => (props.runningTourGuide ? 0 : 1)};

  @media only screen and (max-width: 599px) {
    z-index: 1029;
    display: block;
    opacity: 1;
  }
`;

const StyledContent = styled('div')`
  flex-grow: 1;
  margin: ${(props) =>
    // eslint-disable-next-line no-nested-ternary
    props?.isProjectPage ||
    props?.isPackageExpiringSoon ||
    props?.showNotifyLowCreditsBanner ||
    props?.isEducatorsRoute ||
    (props?.isPricingPage && props?.newPricingOverviewEnabled)
      ? '0px'
      : props?.isMobileTTS
      ? '8px 0px 8px 0px'
      : '8px 16px 8px 16px'};
  min-height: calc(100% - 170px);
  display: flex;
  flex-direction: column;
  .banner {
    width: 100%;
  }

  .content-layout {
    flex-grow: 1;
    /* overflow-x: hidden turns this into a scroll container on both axes, which
       breaks the sticky page headers. Pages that have one clip overflow
       themselves instead. */
    overflow-x: ${(props) =>
      !props?.isPricingPage && !props?.isEducatorsRoute && 'hidden'};
  }
`;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    border: 'none',
    position: 'fixed',
    top: '0',
    bottom: '0',
    left: '0',
    zIndex: '1032',
    transitionProperty: 'top, bottom, width',
    transitionDuration: '.2s, .2s, .35s',
    transitionTimingFunction: 'linear, linear, ease',
    boxShadow: BOX_SHADOW,
    width: SIDEBAR_WIDTH,

    [theme.breakpoints.up('md')]: {
      width: SIDEBAR_WIDTH,
      position: 'fixed',
      height: '100%',
    },

    [theme.breakpoints.down('sm')]: {
      width: SIDEBAR_WIDTH,
      boxShadow: BOX_SHADOW,
      position: 'fixed',
      display: 'block',
      top: '0',
      height: '100vh',
      right: '0',
      left: 'auto',
      zIndex: '1032',
      visibility: 'visible',
      overflowY: 'visible',
      borderTop: 'none',
      textAlign: 'left',
      paddingRight: '0px',
      paddingLeft: '0',
    },

    '&:before,&:after': {
      position: 'absolute',
      zIndex: '3',
      width: '100%',
      height: '100%',
      content: '""',
      display: 'block',
      top: '0',
    },
  },
}));

const StyledNavBar = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  .navbar-item {
    display: flex;
    align-items: center;
    font-size: 14px;
    gap: 10px;
  }

  .btn-order {
    display: flex;
    border-radius: 5px;
  }

  .pending-button {
    color: ${COLOR.dark};

    &:hover,
    &:focus {
      background: ${TRANSPARENT_COLOR.primary};
      color: ${COLOR.darkBlue};
    }
  }

  .error-button {
    color: ${COLOR.danger};
  }

  .upgrade-button {
    color: ${COLOR.primary};
    font-weight: 600;
    background: ${TRANSPARENT_COLOR.primary};

    &:hover {
      background: ${COLOR.primary};
      color: ${COLOR.white};
    }
  }

  .language-icon {
    width: 16px;
  }
  .language-text {
    font-weight: normal;
    text-transform: none;
  }

  .profile {
    display: flex;
    align-items: center;
    gap: 8px;
    .menu-item {
      gap: 10px;
    }
    .style-text {
      text-decoration: none;
      color: ${COLOR.text};
    }
    .profile-info {
      display: flex;
      align-items: center;
      cursor: pointer;
    }
  }
  .profile-content {
    text-align: right;
  }

  .light-text {
    color: ${COLOR.light};
  }
  .text {
    font-size: 14px;
    white-space: nowrap;
  }
  .bold {
    font-weight: bold;
  }
  .error-text {
    color: ${COLOR.danger};
  }

  .profile-icon {
    margin-right: 5px;
  }

  .package-info {
    margin-left: 12px;
  }

  @media only screen and (min-width: 1599px) {
    .large-package-info {
      display: flex;
      gap: 15px;
    }

    .large-language-button,
    .normal-username-info,
    .normal-package-info-item {
      display: flex;
    }

    .medium-language-button,
    .small-username-info,
    .medium-package-info,
    .small-package-info-item {
      display: none;
    }
  }

  @media only screen and (max-width: 1599px) and (min-width: 1439px) {
    .large-package-info {
      display: flex;
      gap: 15px;
    }

    .small-username-info,
    .medium-language-button,
    .normal-package-info-item {
      display: flex;
    }

    .large-language-button,
    .normal-username-info,
    .medium-package-info,
    .small-package-info-item {
      display: none;
    }
  }

  @media only screen and (max-width: 1439px) and (min-width: 1199px) {
    .large-package-info,
    .normal-username-info,
    .medium-language-button,
    .small-package-info-item {
      display: none;
    }

    .large-language-button,
    .small-username-info,
    .normal-package-info-item {
      display: flex;
    }

    .medium-package-info {
      display: grid;
      grid-template-columns: 230px 230px;
    }
  }

  @media only screen and (max-width: 1199px) and (min-width: 1023px) {
    .large-language-button,
    .normal-username-info,
    .large-package-info,
    .small-package-info-item {
      display: none;
    }

    .medium-language-button,
    .small-username-info,
    .normal-package-info-item {
      display: flex;
    }

    .medium-package-info {
      display: grid;
      grid-template-columns: 220px 220px;
    }
  }

  @media only screen and (max-width: 1023px) and (min-width: ${MOBILE_BREAKPOINT}) {
    .large-language-button,
    .normal-username-info,
    .small-username-info,
    .large-package-info,
    .normal-package-info-item {
      display: none;
    }

    .medium-language-button,
    .small-package-info-item {
      display: flex;
    }

    .medium-package-info {
      display: grid;
      grid-template-columns: 190px 190px;
    }
  }

  @media only screen and (max-width: ${MOBILE_BREAKPOINT}) {
    .large-language-button,
    .normal-username-info,
    .small-username-info,
    .large-package-info,
    .normal-package-info-item {
      display: none;
    }

    .normal-username-info {
      display: block;
    }
  }

  .button-avatar {
    position: relative;
    border: 3px solid transparent;
    border-radius: 100%;

    :before {
      content: '';
      position: absolute;
      inset: 0;
      margin: -3px;
      background-color: ${(props) => props.colorpackageavatar};
      border-radius: inherit;
    }
  }
`;

const StyledPolicyAndTermComponent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 16px;
  padding: 10px 30px 10px 10px;
`;

const StyledMenu = styled(Menu)`
  .menu-item {
    grid-gap: 10px;
  }

  .text-link {
    color: ${COLOR.text};
    text-decoration: none;
  }

  .logout-style {
    color: ${COLOR.danger};
  }
  .style-version {
    margin-left: 20px;
    color: ${COLOR.light};
  }

  .icon-download-container {
    margin: 0px 0px 12px -14px;
  }
`;

const StyledDownloadAppContainer = styled('div')`
  padding: 40px 80px;
  overflow: hidden;
  text-align: center;
  .title {
    font-size: 24px;
    font-weight: 700;
  }

  .description {
    font-size: 16px;
    font-weight: 400;
  }

  .description-2 {
    font-size: 16px;
    font-weight: 600;
    margin: 20px 0px;
  }

  .image-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    .qr-code {
      width: 154px;
      height: 154px;
    }
  }
`;

const StyledTooltip = styled(({ className, ...props }) => (
  // eslint-disable-next-line react/jsx-filename-extension
  <Tooltip {...props} classes={{ popper: className }} placement="bottom" />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: COLOR.label[80],
    backdropFilter: 'blur(25px)',
    color: COLOR.white,
    borderRadius: BORDER_RADIUS,
    fontSize: 14,
    fontWeight: 400,
    padding: '8px 10px',
    boxShadow: TOOLTIP_BOX_SHADOW,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: COLOR.label[80],
  },
}));

const StyledCustomerCare = styled('div')`
  position: relative;
  z-index: 1000;
  .customer-care-container {
    position: absolute;
    bottom: 16px;
    right: 24px;
    pointer-events: none;

    .zalo {
      color: ${COLOR.labelBlue};
      font-weight: 600;
      font-size: 12px;
      margin-bottom: 8px;
    }

    .call {
      color: ${COLOR.boldSuccess};
      font-weight: 600;
      font-size: 16px;
    }

    .MuiSpeedDial-actions {
      margin-bottom: 6px;
      padding-bottom: 0px;
    }

    .MuiSpeedDial-fab {
      animation-name: zoom;
      animation-delay: 0s;
      animation-duration: 1.15s;
      animation-iteration-count: infinite;
      border-radius: 100%;
      color: ${COLOR.darkBlue};
      width: 44px;
      height: 44px;

      &:hover {
        animation-name: none;
        background-color: ${COLOR.primary};
      }

      @keyframes zoom {
        0% {
          transform: scale(0.9);
        }

        50% {
          transform: scale(1);
          box-shadow: 0 0 0 8px ${TRANSPARENT_COLOR.primary};
        }

        100% {
          transform: scale(0.9);
          box-shadow: 0 0 0 0 ${TRANSPARENT_COLOR.primary};
        }
      }
    }

    .MuiSpeedDialAction-fab {
      box-shadow: 0px 2px 4px opx rgba(0, 0, 0, 0.1);
      width: 44px;
      height: 44px;

      &:hover {
        background: ${COLOR.white};
      }
    }

    .MuiSpeedDialAction-staticTooltipLabel {
      display: none;
    }
  }
`;

const StyledIconWraper = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 100%;
  text-transform: capitalize;

  .close-icon {
    transform: translate(5px, 3px);
    font-size: 18px;
  }
`;

const StyledPopover = styled(Popover)`
  .MuiPopover-paper {
    overflow: visible;
    margin-left: 10px;

    &::before {
      content: '';
      position: absolute;
      display: block;
      width: 0px;
      left: 5px;
      top: 80%;
      border: 7px solid transparent;
      border-left: 0;
      border-right: 7px solid ${COLOR.white};
      transform: translate(calc(-100% - 5px), -50%);
    }
  }

  .link {
    text-decoration: none;
    color: ${COLOR.text};
  }

  .menu-item {
    padding: 10px 20px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    &:hover {
      background-color: ${TRANSPARENT_COLOR.primary};
      border-radius: 5px;
    }
  }

  .title {
    font-weight: 500;
    font-size: 14px;
  }

  .sub-title {
    font-weight: 300;
    font-size: 12px;
  }
`;

const StyledPackageInfoItem = styled('div')`
  .package-info-item {
    margin-right: 5px;
    gap: 5px;
    align-items: center;
  }

  .lock-icon {
    margin-bottom: 2px;
  }

  .package-info-icon {
    width: 18px;
    height: 18px;
  }
`;

const StyledBetaWrapper = styled('div')`
  position: relative;

  .beta-background {
    background-color: ${COLOR.paleBlue};
    border-radius: 100px;
    width: 40px;
    height: 25px;
    margin-top: 3px;
  }

  .beta-text {
    margin: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    -ms-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
    font-weight: 700;
    color: ${COLOR.info};
    font-size: 12px;
    text-transform: uppercase;
  }
`;

const StyledPopupTransferPackage = styled('div')`
  padding-bottom: 16px;

  .description-transfer-package {
    color: ${COLOR.darkGray};
    font-size: 14px;
    font-weight: 400;
    letter-spacing: -0.4px;
    line-height: 20px;
  }

  .notes {
    margin-top: 16px;

    .note-title {
      font-size: 12px;
      font-weight: 700;
      letter-spacing: -0.4px;
      color: ${COLOR.darkGray};
      line-height: 16px;
      margin-bottom: 4px;
    }

    .note-list {
      color: #1c1c1ea3;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      margin-bottom: 4px;
      font-style: italic;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
  }

  .info-change {
    padding: 16px;
    background-color: #007aff14;
    margin-top: 16px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .info-package {
      font-size: 12px;
      letter-spacing: -0.4px;
      line-height: 16px;
      color: #1c1c1ea3;
      display: flex;
      gap: 2px;
      strong {
        color: #1c1c1e;
      }
    }

    .icon-item {
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 8px;
    }

    .icon-wysiwy {
      font-size: 24px;
      color: #007aff;
    }

    .item {
      display: flex;
      align-items: center;
    }
  }

  .info-transfer-package {
    margin-top: 16px;
    padding: 16px 48px;
    background-color: #f4f5f7;
    border-radius: 8px;
    padding-top: 16px;
    padding-bottom: 16px;
    display: flex;
    align-items: center;
    .icon-arrow-right {
      font-size: 24px;
      color: #007aff;
    }
  }

  .old-package {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;

    .type-package {
      font-size: 14px;
      color: #6b7280;
      letter-spacing: -0.4px;
      line-height: 24px;
    }

    .name-package {
      color: #111827;
      font-weight: 700;
      letter-spacing: -0.4px;
      line-height: 24px;
      font-size: 14px;
      text-align: center;
    }
  }

  .new-package {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;

    .type-package {
      font-size: 14px;
      color: ${COLOR.blueV2};
      letter-spacing: -0.4px;
      line-height: 24px;
      font-weight: 400;
    }

    .name-package {
      color: ${COLOR.blueV2};
      font-weight: 700;
      letter-spacing: -0.4px;
      line-height: 24px;
      font-size: 14px;
      text-align: center;
    }
  }

  .right-here-text {
    cursor: pointer;
    color: ${COLOR.blueV2};
    font-size: 12px;
    font-style: italic;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: -0.4px;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
  }

  .transfer-button {
    display: flex;
    justify-content: center;
    margin-top: 16px;
  }

  .button {
    width: fit-content;
    text-transform: none;
    border-radius: 12px;
    color: ${COLOR.darkBlue};
    font-size: 14px;
    font-weight: 700;
    line-height: 24px;
    word-wrap: break-word;
    padding: 12px 32px;
    background: ${COLOR.primary};
    border: none;
    cursor: pointer;
  }

  @media only screen and (max-width: ${MOBILE_BREAKPOINT}) {
    .info-transfer-package {
      padding: 16px 8px;
      gap: 0px;
    }

    .info-package {
      flex-direction: column;
    }

    .info-change {
      padding: 12px;
      gap: 8px;
    }

    .right-here-text {
      font-size: 11px;
    }

    .notes {
      .note-title {
        font-size: 11px;
      }

      .note-list {
        font-size: 11px;
      }
    }
  }
`;

export {
  StyledPopupTransferPackage,
  StyledWrapper,
  StyledMainPanel,
  StyledContent,
  StyledDrawer,
  StyledNavBar,
  StyledPolicyAndTermComponent,
  StyledMenu,
  StyledDownloadAppContainer,
  StyledTooltip,
  StyledCustomerCare,
  StyledPackageInfoItem,
  StyledPopover,
  StyledBetaWrapper,
  StyledIconWraper,
  StyledNewAppBar,
  StyledWrapperExpiringSoon,
};
